import type { CatalogIngestMode, CatalogIngestRun } from '@avrahami/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { catalogSummaryQuery, ingestRunsQuery, keys, platformApi } from '../api/platform';
import { hasCapability, sessionQuery } from '../api/session';
import { Stat } from '../components/Stat';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cell,
  ConfirmDialog,
  formatDateTime,
  Loading,
  PageHeader,
  Table,
} from '@avrahami/ui';

const statusTone = { RUNNING: 'blue', SUCCEEDED: 'green', FAILED: 'red' } as const;

function Summary() {
  const summary = useQuery(catalogSummaryQuery);
  if (summary.isPending) return <Loading />;
  if (summary.isError) return <Alert>{errorMessage(summary.error)}</Alert>;
  const { products, categories, mediaFiles, lastSucceededAt } = summary.data;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label={he.catalog.activeProducts} value={products.active} />
      <Stat label={he.catalog.discontinued} value={products.discontinued} />
      <Stat label={he.catalog.withoutImage} value={products.withoutImage} />
      <Stat label={he.catalog.withoutPrice} value={products.withoutPrice} />
      <Stat label={he.catalog.bundles} value={products.bundles} />
      <Stat label={he.catalog.categories} value={categories.active} />
      <Stat label={he.catalog.mediaFiles} value={mediaFiles} />
      <Stat
        label={he.catalog.lastSuccess}
        value={lastSucceededAt === null ? he.catalog.never : formatDateTime(lastSucceededAt)}
      />
    </div>
  );
}

function RunRow({ run }: { run: CatalogIngestRun }) {
  const { counts } = run;
  return (
    <tr data-testid="ingest-run" data-status={run.status}>
      <Cell className="whitespace-nowrap">{formatDateTime(run.startedAt)}</Cell>
      <Cell>{he.catalog.modes[run.mode]}</Cell>
      <Cell>
        <Badge tone={statusTone[run.status]}>{he.catalog.statuses[run.status]}</Badge>
      </Cell>
      <Cell className="ltr text-center">
        {counts.productsCreated} / {counts.productsUpdated} / {counts.productsDiscontinued}
      </Cell>
      <Cell className="ltr text-center">{counts.productsRejected}</Cell>
      <Cell className="ltr text-center">
        {counts.mediaCopied} / {counts.mediaFailed}
      </Cell>
      <Cell className="text-xs text-red-700">
        {run.errorCode === null ? null : he.catalog.errors[run.errorCode]}
      </Cell>
    </tr>
  );
}

function Runs() {
  const runs = useInfiniteQuery(ingestRunsQuery());
  const items = runs.data?.pages.flatMap((page) => page.items) ?? [];
  if (runs.isPending) return <Loading />;
  if (runs.isError) return <Alert>{errorMessage(runs.error)}</Alert>;
  if (items.length === 0) return <p className="text-sm text-slate-500">{he.catalog.noRuns}</p>;
  return (
    <>
      <Table
        head={[
          he.catalog.started,
          he.catalog.mode,
          he.catalog.status,
          he.catalog.products,
          he.catalog.rejected,
          he.catalog.images,
          he.catalog.error,
        ]}
      >
        {items.map((run) => (
          <RunRow key={run.id} run={run} />
        ))}
      </Table>
      {runs.hasNextPage ? (
        <div className="mt-4">
          <Button
            tone="secondary"
            busy={runs.isFetchingNextPage}
            onClick={() => {
              void runs.fetchNextPage();
            }}
          >
            {he.common.loadMore}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/** Avrahami's master catalog: what is in it, its ingest history, and "update now". */
export function CatalogPage() {
  const queryClient = useQueryClient();
  const session = useQuery(sessionQuery);
  const canIngest =
    session.data !== undefined && hasCapability(session.data, 'platform.catalog.ingest');
  const [confirmFull, setConfirmFull] = useState(false);
  const request = useMutation({
    mutationFn: (mode: CatalogIngestMode) => platformApi.requestCatalogIngest(mode),
    onSuccess: () => {
      setConfirmFull(false);
      void queryClient.invalidateQueries({ queryKey: keys.catalog });
    },
  });

  return (
    <>
      <PageHeader
        title={he.catalog.title}
        actions={
          canIngest ? (
            <div className="flex gap-2">
              <Button
                busy={request.isPending && request.variables === 'INCREMENTAL'}
                onClick={() => {
                  request.mutate('INCREMENTAL');
                }}
              >
                {he.catalog.runNow}
              </Button>
              <Button
                tone="secondary"
                onClick={() => {
                  setConfirmFull(true);
                }}
              >
                {he.catalog.runFull}
              </Button>
            </div>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-6">
        <p className="text-sm text-slate-600">{he.catalog.intro}</p>
        {request.isError ? <Alert>{errorMessage(request.error)}</Alert> : null}
        {request.data === undefined ? null : (
          <Alert tone="info">
            {request.data.queued ? he.catalog.queued : he.catalog.alreadyQueued}
          </Alert>
        )}
        <Card title={he.catalog.summary}>
          <Summary />
        </Card>
        <Card title={he.catalog.runs}>
          <Runs />
        </Card>
      </div>
      <ConfirmDialog
        open={confirmFull}
        title={he.catalog.fullTitle}
        confirmLabel={he.catalog.runFull}
        busy={request.isPending}
        onConfirm={() => {
          request.mutate('FULL');
        }}
        onClose={() => {
          setConfirmFull(false);
        }}
      >
        {he.catalog.fullBody}
      </ConfirmDialog>
    </>
  );
}
